/**
 * Partner coupon codes at checkout (docs/partners.md).
 *
 * A code arrives two ways: typed on the Plans page or the upgrade dialog, or
 * on a link (`/register?coupon=IPHONE-P1`, or `?coupon=` on any route). A
 * link's code is kept in localStorage for 30 days, so it survives signing up
 * and onboarding, and is pre-filled and checked on the Plans page.
 *
 * This module reads `?coupon=` from the address once when it is first
 * imported (the app imports it at start-up through the billing and auth
 * pages), before any route guard can redirect the query away.
 */
import { create } from 'zustand'

import { ApiError } from '@/api/client'
import { billingApi } from '@/api/endpoints'
import type { BillingPeriod } from '@/types/api'

const KEY = 'jobslide.coupon'
const TTL_MS = 30 * 24 * 60 * 60 * 1000
const CODE = /^[A-Z0-9-]{4,24}$/

export interface AppliedCoupon {
  code: string
  discountPct: number
  duration: 'once' | 'forever'
  message: string
}

/** Codes are uppercase A–Z, 0–9 and `-`. */
export function normaliseCode(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, '')
}

// ---- Storage (30 days; private mode or blocked storage just means no memory) -----------------
export function readStoredCoupon(): string | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const saved = JSON.parse(raw) as { code?: unknown; savedAt?: unknown }
    if (typeof saved.code !== 'string' || typeof saved.savedAt !== 'number' || Date.now() - saved.savedAt > TTL_MS) {
      localStorage.removeItem(KEY)
      return null
    }
    return saved.code
  } catch {
    return null
  }
}

export function storeCoupon(code: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ code, savedAt: Date.now() }))
  } catch {
    /* storage unavailable */
  }
}

export function clearStoredCoupon(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* storage unavailable */
  }
}

/** Saves `?coupon=CODE` from the address, when it is a well-formed code. Returns the code. */
export function captureCouponFromUrl(search: string = typeof window === 'undefined' ? '' : window.location.search): string | null {
  try {
    const raw = new URLSearchParams(search).get('coupon')
    if (!raw) return null
    const code = normaliseCode(raw)
    if (!CODE.test(code)) return null
    storeCoupon(code)
    return code
  } catch {
    return null
  }
}

captureCouponFromUrl()

// ---- Prices ---------------------------------------------------------------------------------
export function discountedCents(amountCents: number, discountPct: number): number {
  return Math.round((amountCents * (100 - discountPct)) / 100)
}

/** "first month" / "first quarter" / "first year". */
export const FIRST_PERIOD: Record<BillingPeriod, string> = {
  monthly: 'first month',
  quarterly: 'first quarter',
  annual: 'first year',
}

// ---- State shared by the Plans page, the upgrade dialog and checkout --------------------------
export const NEW_SUBSCRIPTIONS_ONLY =
  'Coupon codes work for new subscriptions. You already pay for a plan, so this code cannot be used to change it.'

interface CouponState {
  draft: string
  applied: AppliedCoupon | null
  /** The inline message under the field (an invalid code, or a checkout refusal). */
  error: string | null
  checking: boolean
  /** True once the stored code has been checked on this page load. */
  autoTried: boolean
  setDraft: (value: string) => void
  apply: (code?: string) => Promise<void>
  remove: () => void
  setError: (message: string | null) => void
  /** Checks the code a link left behind, once. */
  applyStored: () => void
}

export const useCouponStore = create<CouponState>((set, get) => ({
  draft: readStoredCoupon() ?? '',
  applied: null,
  error: null,
  checking: false,
  autoTried: false,

  setDraft: (value) => set({ draft: normaliseCode(value).slice(0, 24), error: null }),

  apply: async (input) => {
    const code = normaliseCode(input ?? get().draft)
    if (!code) return
    if (!CODE.test(code)) {
      set({ applied: null, error: 'Codes are 4 to 24 letters, digits or dashes.' })
      return
    }
    set({ checking: true, error: null, draft: code })
    try {
      const result = await billingApi.validateCoupon(code)
      if (result.valid) {
        const applied: AppliedCoupon = {
          code: result.code || code,
          discountPct: Number(result.discountPct) || 0,
          duration: result.duration === 'forever' ? 'forever' : 'once',
          message: result.message,
        }
        storeCoupon(applied.code)
        set({ applied, error: null, checking: false, draft: applied.code })
      } else {
        // A dead code should not be offered again on the next visit.
        if (readStoredCoupon() === code) clearStoredCoupon()
        set({ applied: null, error: result.message || 'This code cannot be used.', checking: false })
      }
    } catch (error) {
      set({
        applied: null,
        checking: false,
        error: error instanceof ApiError && error.status < 500 ? error.message : 'The code could not be checked. Try again in a moment.',
      })
    }
  },

  remove: () => {
    clearStoredCoupon()
    set({ applied: null, draft: '', error: null })
  },

  setError: (message) => set({ error: message }),

  applyStored: () => {
    const state = get()
    if (state.autoTried || state.applied || state.checking) return
    set({ autoTried: true })
    const stored = readStoredCoupon()
    if (stored) void state.apply(stored)
  },
}))

/** Called after a successful checkout: the code has done its job. */
export function forgetCoupon(): void {
  clearStoredCoupon()
  useCouponStore.setState({ applied: null, draft: '', error: null })
}
